import { Navigate, useLocation } from '@modern-js/runtime/router';
import { useContext, useEffect, useRef } from 'react';

import GlobalStore from '@store/globalStore';
import { useModel } from '@modern-js/runtime/model';
import { useCookieState } from 'ahooks';
import { GlobalContext } from './context';
import { fetchUserInfo } from '@/api/meeting_account_api';
import useUrlParams from '@/hooks/useUrlParams';
import { AUTH_STATUS_KEY, AuthorizationStatus } from '@/config/constant';
import { loadOfficeSession } from '@/views/office-assistant/interaction/historyApi';

export const UserAuthProvider = ({ children }: { children: JSX.Element }) => {
  const { resetToken = '' } = useUrlParams();
  const location = useLocation();
  const { setUserInfo } = useContext(GlobalContext);
  const [, actions] = useModel(GlobalStore);
  const [, setMessage] = useCookieState(AUTH_STATUS_KEY);
  // Hook-provided setters may change identity; they must not retrigger user loading.
  const callbacksRef = useRef({ setStoreUser: actions.setUser, setTrackerUser: setUserInfo, setAuthMessage: setMessage });

  useEffect(() => {
    callbacksRef.current = { setStoreUser: actions.setUser, setTrackerUser: setUserInfo, setAuthMessage: setMessage };
  }, [actions.setUser, setMessage, setUserInfo]);

  useEffect(() => {
    let cancelled = false;
    const cancelPendingRequest = () => {
      cancelled = true;
    };
    const isPasswordExpired = localStorage.getItem('PASSWORD_EXPIRED') === '1';
    const isPwdResetPath = location.pathname.includes('/pwd/reset');
    const isLoginPath = location.pathname.includes('/login');
    const isPwdPath = location.pathname.includes('/pwd');
    const isRegPath = location.pathname.includes('/reg');
    const isInvitePath = location.pathname.includes('/invite');

    // 如果密码过期且不在密码重置页面和登录页，清除授权并跳转登录页
    if (isPasswordExpired && !isLoginPath && !isPwdPath && !isRegPath && !isInvitePath) {
      localStorage.removeItem('PASSWORD_EXPIRED');
      localStorage.clear();
      sessionStorage.clear();
      callbacksRef.current.setAuthMessage(AuthorizationStatus.Authenticated);
      window.location.replace('/login');
      return cancelPendingRequest;
    }

    // 获取用户信息
    if ((!isPwdPath || isPwdResetPath) && !isRegPath && !isInvitePath && !resetToken) {
      const applyUser = (userInfo: Record<string, any>, meetingOnlyUser?: number) => {
        if (cancelled) {
          return;
        }
        callbacksRef.current.setTrackerUser?.({ userUniqueId: userInfo.accountId, userId: userInfo.accountId, meetingOnlyUser });
        callbacksRef.current.setStoreUser({ ...userInfo, meetingOnlyUser });
        localStorage.setItem('userInfo', JSON.stringify(userInfo || {}));
      };
      const loadMeetingUser = () =>
        fetchUserInfo({})
          .then(({ userInfo = {}, meetingOnlyUser }) => {
            applyUser(userInfo, meetingOnlyUser);
          })
          .catch(error => {
            if (!cancelled) {
              console.error('Failed to load the authenticated user:', error);
            }
          });
      if (location.pathname === '/agent' || location.pathname.startsWith('/agent/')) {
        loadOfficeSession()
          .then(({ user_id: userId }) => {
            let cached: Record<string, any> = {};
            try {
              cached = JSON.parse(localStorage.getItem('userInfo') || '{}');
            } catch {
              // A valid signed Office session is sufficient to restore this route.
            }
            const userInfo = String(cached.accountId || '') === userId ? cached : { accountId: userId };
            callbacksRef.current.setAuthMessage(AuthorizationStatus.Authenticated);
            applyUser(userInfo);
          })
          .catch(loadMeetingUser);
      } else {
        loadMeetingUser();
      }
    }

    return cancelPendingRequest;
  }, [location.pathname, resetToken]);

  return children;
};

export const RequireAuth = ({ children }: { children: JSX.Element }) => {
  const location = useLocation();

  const [message] = useCookieState(AUTH_STATUS_KEY);

  if (!Number(message)) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};
